import { IFieldBaseProps } from "common/components/Field";
import { IRadioProps } from "common/components/Radio";
import React, { ChangeEventHandler } from "react";
import { useLocation } from "react-router";

export interface RadioControlProps extends Omit<IRadioProps, "onChange" | "type" | "name"> {
  size?: "sm" | "md";
  label: string;
  isChecked: boolean;
  borderless?: boolean;
  noBackground?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
}

export const RadioControl: React.FC<RadioControlProps> = ({
  id,
  label,
  disabled,
  size = "md",
  isChecked,
  borderless = true,
  noBackground = false,
  onChange,
}) => {
  // Hooks
  const { hash } = useLocation();
  const secondHash = hash?.split("#")[2];
  const isHash = secondHash?.replaceAll("-", " ") === label.toLowerCase();

  // Constants
  let wrapperClassName = `flex items-center gap-xs pb-xs rounded-3xl mt-xs
  border-secondary-200 text-secondary-900 group
  peer-focus:ring-[3px] peer-focus:ring-secondary-50 focus:outline-none
  peer-disabled:cursor-not-allowed peer-disabled:text-secondary-500 peer-disabled:border-secondary-50 peer-enabled:cursor-pointer`;

  const iconSizes = {
    sm: "text-[14px]",
    md: "text-[16px]",
  };
  const labelSizes = {
    sm: "leading-[16px] text-sm",
    md: "leading-[20px] text-base",
  };
  const ownSizes = {
    sm: "h-[24px]",
    md: "h-[32px]",
  };
  const ownClassName = `flex w-fit ${ownSizes[size]} ${isHash ? "bg-primary-50" : ""}`;

  const labelClassName = `select-none ${labelSizes[size]}`;
  const iconClassName = `material-symbols-outlined ${iconSizes[size]} ${
    disabled ? "!text-secondary-300" : isChecked ? "text-secondary-900" : "text-secondary-300"
  } group-hover:text-secondary-900`;

  if (borderless) {
    wrapperClassName = wrapperClassName.concat(" ", "border-none");
  }

  if (!noBackground) {
    wrapperClassName = `${wrapperClassName} px-md peer-enabled:hover:bg-secondary-50 border pt-xs`;
  }

  const icon = isChecked ? "radio_button_checked" : "radio_button_unchecked";

  return (
    <label htmlFor={id} className={ownClassName}>
      <input id={id} type="radio" disabled={disabled} className="hidden peer" onChange={onChange} checked={isChecked} />
      <div className={wrapperClassName}>
        <span className={iconClassName}>{icon}</span>
        <div className={labelClassName}>{label}</div>
      </div>
    </label>
  );
};
